import { useContext } from 'react';
import { Navigate } from 'react-router-dom';

import { StatusLabel } from '@shared/components';
import { useDocumentTitle } from '@shared/hooks';
import { TERMINOLOGY } from '@shared/i18n';
import { SignInContext } from '@modules/users';
import { TFolder, useFolder } from '@modules/items';

import LiteraturesContent from '../components/LiteraturesContent';

// Flat route: `/literatures`. Literature conceptually lives under the QM /
// global-workspace folder, so the nav drawer is driven by the QM folder -
// not the literature folder. The literature folder itself is still needed
// for the backend-enforced EDIT/MANAGE checks (Create, Access).
const Literatures = () => {
  const { organization } = useContext(SignInContext);
  // All three must be present: the flag, the literature folder (backend
  // checkLiteratureFolder rejects requests without it), and the QM folder
  // (drives nav drawer + breadcrumbs - literature lives under QM).
  const isEnabled =
    !!organization.featureSettings?.literatures?.enabled &&
    !!organization.folders.literature &&
    !!organization.folders.qm;

  useDocumentTitle(TERMINOLOGY.literature.upper);

  const { error: qmError, data: qmData } = useFolder(organization.folders.qm, { enabled: isEnabled });
  const { error: litError, data: litData } = useFolder(organization.folders.literature, {
    enabled: isEnabled,
  });

  if (!isEnabled) return <Navigate to="/" replace />;

  const bothLoaded = qmData && litData;
  const firstError = qmError ?? litError;

  return (
    <StatusLabel
      data={bothLoaded ? qmData : undefined}
      loadingMessage="Loading workspace..."
      error={firstError}
      errorMessage="Loading workspace failed!"
      render={() => <LiteraturesContent qmFolder={qmData as TFolder} literatureFolder={litData as TFolder} />}
    />
  );
};

export default Literatures;
